/** Hooks React Query pour la saisie + calcul DPE. */

import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  calculateDpeStreamed,
  type CalculateProgress,
  getDpe,
  updateDpePayload,
  type DPE,
} from "@/api/dpe";
import { getCatalogs } from "@/api/catalogs";

const DPE_KEY = (logementId: string) => ["dpe", logementId] as const;
const CATALOGS_KEY = ["catalogs"] as const;

export function useDpe(logementId: string) {
  return useQuery<DPE>({
    queryKey: DPE_KEY(logementId),
    queryFn: () => getDpe(logementId),
    enabled: !!logementId,
  });
}

export function useCatalogs() {
  return useQuery({
    queryKey: CATALOGS_KEY,
    queryFn: getCatalogs,
    staleTime: 5 * 60_000, // 5 minutes — catalogues immuables
  });
}

export function useSaveDpePayload(logementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateDpePayload(logementId, payload),
    onSuccess: (data) => {
      qc.setQueryData(DPE_KEY(logementId), data);
    },
  });
}

/** Barre de progression du calcul (null hors calcul / 0..100 pendant). */
export type CalculateProgressState = CalculateProgress | null;

/**
 * Variante streaming du calcul DPE — expose une barre de progression réelle
 * (pourcentage par étape du moteur 3CL, via SSE backend).
 *
 * Préserve l'API historique (`mutate`, `isPending`, `onSuccess`/`onError`)
 * consommée par la page de saisie, et ajoute `progress` + `reset` (abort).
 */
export function useCalculateDpe(logementId: string) {
  const qc = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [progress, setProgress] = useState<CalculateProgressState>(null);
  const abortRef = useRef<AbortController | null>(null);

  const invalidateAfterCalc = () => {
    qc.invalidateQueries({ queryKey: ["logements"] });
    // CRITIQUE : le composant <ReportView> fetch /report-data via une query
    // distincte cachée par TanStack. Sans invalidation, l'UI affiche le
    // rapport précédent même après recalcul → impression que "rien ne
    // change" malgré une saisie modifiée.
    qc.invalidateQueries({ queryKey: ["report-data", logementId] });
  };

  const mutate = (
    _vars: undefined,
    options?: {
      onSuccess?: (data: DPE) => void;
      onError?: (err: unknown) => void;
    },
  ) => {
    setIsPending(true);
    setProgress(null);
    const ac = new AbortController();
    abortRef.current = ac;
    calculateDpeStreamed(logementId, {
      onProgress: (p) => setProgress(p),
      signal: ac.signal,
    })
      .then((data) => {
        qc.setQueryData(DPE_KEY(logementId), data);
        invalidateAfterCalc();
        setIsPending(false);
        setProgress(null);
        options?.onSuccess?.(data);
      })
      .catch((err: unknown) => {
        setIsPending(false);
        setProgress(null);
        options?.onError?.(err);
      });
  };

  const reset = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsPending(false);
    setProgress(null);
  };

  return { mutate, isPending, progress, reset };
}